<script setup lang="ts">
//#region imports
import { useI18n } from 'vue-i18n';
import { computed, nextTick, ref } from 'vue';
import { helpers, minLength, required, requiredIf } from '@vuelidate/validators';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useValidate } from '@/composables/useValidate';
import { useRulesValidator } from '@/composables/useRulesValidator';
import { usePermissions } from '@/composables/usePermissions';
import { useLodash } from '@/setup/lodash';
import { typeGuard, type ToErrorMap , type EmitsToListeners, type ErrorList, type ErrorMessage } from '@/types/core';
import type { OverrideFields } from '@/types/util';
import type {
  GetAffiliatesData,
  GetAffiliatesResponse,
  GetMarketingMaterialsByIdResponse, PostFilesData,
  PostFilesResponse,
  PostMarketingMaterialsData,
  PutMarketingMaterialsByIdData,
} from '@/types/openapi-ts';
import type { UploadRequestOptions } from 'element-plus/es/components/upload/src/upload';
//#endregion

//#region types
interface Props {
  materialId?: string | null,
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface MarketingMaterialParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type AssetsType = OverrideFields<GetMarketingMaterialsByIdResponse['payload']['assets'][number], {
  width: string,
  height: string,
}>;

type FormType = OverrideFields<PostMarketingMaterialsData['body'], {
  id?: string,
  assets: AssetsType[],
}>;

type FormErrors = ToErrorMap<PutMarketingMaterialsByIdData['body'] & PostMarketingMaterialsData['body']>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  materialId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const _ = useLodash();
const { t } = useI18n();
const { isLandingPath } = useRulesValidator();
const { hasPermission } = usePermissions();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<FormErrors>({});

const affiliatesList = ref<GetAffiliatesResponse['payload']>([]);
const currentAffiliates = ref([]);
const uploadingFiles = ref(0);
const uploadPercentage = ref(0);

const form = ref<FormType>({
  name: '',
  status: 'active',
  type: 'simple_banner',
  supported_platforms: [],
  landing_path: '',
  brand_visibility: 'all',
  visible_brands: null,
  visibility: 'public',
  affiliate_ids: [],
  assets: [],
});
//#endregion

//#region computed
const languages = computed(() => store.getters['misc/languages']);
const brands = computed(() => store.getters['misc/brands']);

const isVisibility = computed(() => {
  return hasPermission('affiliates_view');
});

const isEdit = computed(() => {
  return !!props.materialId;
});

const pPopupTitle = computed(() => {
  return `${t(`marketingMaterials.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('marketingMaterials.popup.title')}`;
});

const acceptedTypes = computed(() => {
  let result = '.png, .jpeg, .jpg, .gif';
  if(form.value.type === 'landing') result = '.zip';
  return result;
});

const rules = computed(() => {
  return {
    name: {
      required,
    },
    landing_path: {
      required: requiredIf(() => form.value.type === 'landing'),
      isLandingPath,
    },
    supported_platforms: {
      required: requiredIf(() => form.value.type === 'landing'),
    },
    visible_brands: {
      required: requiredIf(() => form.value.brand_visibility === 'only_selected'),
      minLength: minLength(1),
    },
    affiliate_ids: {
      required: requiredIf(() => form.value.visibility === 'private'),
      minLength: minLength(1),
    },
    assets: {
      $each: helpers.forEach({
        width: {
          required: requiredIf(() => form.value.type !== 'landing'),
        },
        height: {
          required: requiredIf(() => form.value.type !== 'landing'),
        },
        language_codes: {
          required,
          minLength: minLength(1),
        },
      }),
    },
  };
});
//#endregion

//#region methods
async function open() {
  if(isEdit.value) {
    const { data: { payload } } = await api.getMarketingMaterialById(props.materialId!);
    const {
      id,
      name,
      type,
      status,
      supported_platforms,
      brand_visibility,
      visibility,
      affiliates,
      assets,
      visible_brands,
    } = payload;

    form.value = {
      id,
      name,
      type,
      status,
      supported_platforms,
      brand_visibility,
      visibility,
    } as FormType;

    if('landing_path' in payload) form.value.landing_path = payload.landing_path;

    form.value.affiliate_ids = _.map(affiliates, 'id');
    affiliatesList.value = affiliates;
    // currentAffiliates.value = affiliates;
    form.value.visible_brands = _.map(visible_brands, 'site_id');
    form.value.assets = assets.map((asset) => {
      const { language_codes, file_id, file } = asset;
      const [width, height] = 'sizes' in asset && type !== 'landing' && asset.sizes[0] ? asset.sizes[0].split('x') : [];
      return {
        file_id,
        width: width,
        height: height,
        language_codes: language_codes.map((code) => code!.toLowerCase()),
        file,
      } as AssetsType;
    });

  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};
  let promise;
  const {
    name,
    status,
    visibility,
    affiliate_ids,
    visible_brands,
    brand_visibility,
    type,
    assets,
    landing_path,
    supported_platforms,
  } = form.value;

  const assetsTransform = assets.map(
    ({ file_id, width, height, language_codes }) => (form.value.type !== 'landing'
      ? {
        file_id,
        sizes: [`${Number(width)}x${Number(height)}`],
        language_codes,
      }
      : {
        file_id,
        language_codes,
      }),
  );

  let params = {
    name,
    status,
    visibility,
    affiliate_ids,
    visible_brands,
    brand_visibility,
    assets: assetsTransform,
  } as PostMarketingMaterialsData['body'];

  if (!isEdit.value) {
    params.type = type;
  }
  if (form.value.type === 'landing') {
    params.supported_platforms = supported_platforms;
    if(!isEdit.value) params.landing_path = landing_path;
  }

  if(isEdit.value) {
    promise = api.updateMarketingMaterial(form.value.id!, params as PutMarketingMaterialsByIdData['body']);
  } else {
    promise = api.createMarketingMaterial(params);
  }

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

function checkBrands() {
  errors.value = {};
  form.value.visible_brands = null;
}

function checkAffiliates() {
  errors.value = {};
  form.value.affiliate_ids = [];
}

function getAffiliates(search: string) {
  const query = {
    limit: 100,
    offset: 0,
    search,
    account_status: ['approved'],
  } as unknown as GetAffiliatesData['query'];
  api.getAffiliates(query).then((res) => {
    affiliatesList.value = res.data.payload;
  });
}

function isImage(file: PostFilesResponse['payload'][number] | AssetsType['file']) {
  return !!file.preview_url;
}

function uploadFile(assetId: number | undefined = undefined) {
  return async (request: UploadRequestOptions) => {
    const formData = new FormData();
    formData.append('files', request.file);

    const onProgressUploadHandler = (progressEvent: ProgressEvent) => {
      uploadPercentage.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    };

    uploadingFiles.value += 1;
    const { data: { payload: { files } } } = await api.postFile(formData as unknown as PostFilesData['body'], onProgressUploadHandler);
    uploadingFiles.value -= 1;
    uploadPercentage.value = 0;

    if (assetId !== undefined) {
      errors.value = {};
      form.value.assets[assetId] = {
        ...form.value.assets[assetId]!,
        file_id: files!.id,
        width: isImage(files!) ? files!.metadata.width : form.value.assets[assetId]!.width,
        height: isImage(files!) ? files!.metadata.height : form.value.assets[assetId]!.height,
        file: _.omit(files, ['metadata']) as AssetsType['file'],
      };
    } else {
      form.value.assets.push({
        file_id: files!.id,
        width: isImage(files!) ? files!.metadata.width : undefined,
        height: isImage(files!) ? files!.metadata.height : undefined,
        language_codes: [],
        file: _.omit(files, ['metadata']) as AssetsType['file'],
      });
    }
  };
}

function removeAsset(index: number) {
  form.value.assets.splice(index, 1);
  errors.value = {};
}

function generateErrorMessage(errors?: ErrorMessage) {
  if(typeGuard<ErrorList[]>(errors, (errors) => errors && Array.isArray(errors))) {
    const errorMessages = errors.reduce((acc: string[], error) => {
      acc.push(error.message);

      return acc;
    }, []);

    return errorMessages.join('\n');
  }
  
  return errors;
}
//#endregion

//#region validate
const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules: rules,
  form: form,
});
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="marketing-material-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :label="$t('marketingMaterials.popup.form.name.label')"
          :placeholder="$t('marketingMaterials.popup.form.name.placeholder')"
          required
          autosize
          size="big"
          :is-error="isInvalidField('name')"
          @blur="checkField('name')"
        />
        <CardTitle
          :title="$t('marketingMaterials.popup.form.status.label')"
        >
          <UiSwitch
            v-model="form.status"
            :status-text="$t(`marketingMaterials.popup.form.status.${form.status}`)"
            :status-type="form.status === 'active' ? 'positive' : 'negative'"
            active-value="active"
            inactive-value="inactive"
          />
        </CardTitle>
        <CardTitle
          v-if="!isEdit"
          :title="$t('marketingMaterials.popup.form.type.label')"
        >
          <UiRadioGroup
            v-model="form.type"
            :options="[
              {
                label: $t(`marketingMaterials.popup.form.bannerType.simple_banner`),
                value: 'simple_banner',
              },
              {
                label: $t(`marketingMaterials.popup.form.bannerType.html5_banner`),
                value: 'html5_banner',
              },
              {
                label: $t('marketingMaterials.popup.form.type.landing'),
                value: 'landing',
              }
            ]"
          />
        </CardTitle>
        <template
          v-if="form.type === 'landing'"
        >
          <CardTitle
            :title="$t('marketingMaterials.popup.form.supported_platforms.label')"
          >
            <UiCheckboxGroup
              v-model="form.supported_platforms"
              :options="[
                {
                  label: $t('marketingMaterials.popup.form.supported_platforms.mobile'),
                  value: 'mobile',
                },
                {
                  label: $t('marketingMaterials.popup.form.supported_platforms.desktop'),
                  value: 'desktop',
                }
              ]"
            />
          </CardTitle>
          <UiInput
            v-model="form.landing_path"
            class="t:mb-16"
            :label="$t(`marketingMaterials.popup.form.landing_path.label`)"
            :required="!isEdit"
            :placeholder="$t(`marketingMaterials.popup.form.landing_path.placeholder`)"
            autosize
            :disabled="isEdit"
            size="big"
            :is-error="isInvalidField('landing_path')"
            :error="errors.landing_path"
            @blur="checkField('landing_path')"
          />
        </template>
        <UiRadioGroup
          v-model="form.brand_visibility"
          :label="$t('affiliates.offer_popup.brands')"
          :options="[
            {
              label: $t('affiliates.offer_popup.brands_all'),
              value: 'all',
            },
            {
              label: $t('affiliates.offer_popup.brands_only_selected'),
              value: 'only_selected',
            }
          ]"
          @change="checkBrands"
        >
          <UiSelectElement
            v-model="form.visible_brands"
            :placeholder="$t(`affiliates.offer_popup.add_brands`)"
            :list="brands"
            filterable
            multiple
            search-param="name"
            collapse-tag-tooltip
            :max-collapse-tags="3"
            size="big"
            :disabled="form.brand_visibility === 'all'"
            :is-error="isInvalidField('visible_brands')"
          />
        </UiRadioGroup>
        <UiRadioGroup
          v-if="isVisibility"
          v-model="form.visibility"
          :label="$t('marketingMaterials.popup.form.visibility.label')"
          :options="[
            {
              label: $t('marketingMaterials.popup.form.visibility.public'),
              value: 'public',
            },
            {
              label: $t('marketingMaterials.popup.form.visibility.private'),
              value: 'private',
            },
            {
              label: $t('marketingMaterials.popup.form.visibility.hidden'),
              value: 'hidden',
            }
          ]"
          @change="checkAffiliates"
        >
          <UiSelectElement
            v-model="form.affiliate_ids"
            :placeholder="$t('marketingMaterials.popup.form.visibility.placeholder')"
            :list="affiliatesList"
            :remote-method="getAffiliates"
            remote
            filterable
            multiple
            collapse-tag-tooltip
            :max-collapse-tags="1"
            size="big"
            option-label-param="email"
            option-name-param="email"
            :disabled="form.visibility !== 'private'"
          />
        </UiRadioGroup>
        <UiSeparator />
        <div class="t:space-y-8">
          <UiLabel
            :label="$t('marketingMaterials.popup.form.upload.label')"
          />
          <ElUpload
            class="marketing-material-popup__upload"
            drag
            :multiple="form.type !== 'landing'"
            action=""
            :show-file-list="false"
            :http-request="uploadFile()"
            :accept="acceptedTypes"
            :disabled="!!uploadingFiles"
          >
            <div
              v-if="uploadingFiles"
              class="marketing-material-popup__upload-progress"
            >
              <div class="t:mb-4">
                {{ $t('marketingMaterials.messages.uploading') }}
              </div>
              <ElProgress
                :percentage="uploadPercentage"
                color="var(--text-positive)"
              />
            </div>
            <div
              v-else
              class="marketing-material-popup__upload-content"
              v-html="$t('marketingMaterials.messages.text-for-upload')"
            />
          </ElUpload>
          <div
            v-for="(asset, index) in form.assets"
            :key="asset.file_id"
            class="marketing-material-popup__assets"
          >
            <div class="marketing-material-popup__assets-preview">
              <img
                v-if="isImage(asset.file)"
                :src="asset.file.preview_url!"
                :title="asset.file.filename"
                alt=""
              >
              <UiIcon
                v-else
                :size="48"
                name="file-archive"
                color="var(--text-body-tertiary)"
              />
            </div>
            <div class="t:px-16 t:py-12 t:space-y-12 t:flex-1">
              <div class="t:flex t:items-center t:justify-between">
                <div class="t:flex t:items-center t:space-x-8">
                  <UiLabel
                    :label="asset.file.filename"
                    class="t:max-w-[260px]"
                    truncate
                    bold
                  />
                  <ElUpload
                    action=""
                    :show-file-list="false"
                    :http-request="uploadFile(index)"
                    :accept="acceptedTypes"
                    :disabled="!!uploadingFiles"
                  >
                    <ActionIcon
                      :tooltip="$t('marketingMaterials.messages.replace_file')"
                      color="var(--text-primary)"
                      icon="sync"
                      :is-overlay="false"
                    />
                  </ElUpload>
                  <a :href="asset.file.download_url">
                    <ActionIcon
                      :tooltip="$t('marketingMaterials.messages.download_file')"
                      color="var(--text-primary)"
                      icon="download"
                      :is-overlay="false"
                    />
                  </a>
                </div>
                <ActionIcon
                  color="var(--text-negative)"
                  icon="times"
                  :is-overlay="false"
                  @click="removeAsset(index)"
                />
              </div>
              <div class="t:flex t:items-start t:space-x-16">
                <div
                  v-if="form.type !== 'landing'"
                  class="t:flex t:items-start t:space-x-16"
                >
                  <UiInput
                    v-model.number="form.assets[index]!.width"
                    :label="$t(`marketingMaterials.popup.form.width.label`)"
                    required
                    width="53px"
                    :is-error="isInvalidField(`assets.$each.$response.$errors[${index}].width`)"
                    @blur="checkField(`assets.$each.$response.$errors[${index}].width`)"
                  />
                  <UiInput
                    v-model.number="form.assets[index]!.height"
                    :label="$t(`marketingMaterials.popup.form.height.label`)"
                    required
                    width="53px"
                    :is-error="isInvalidField(`assets.$each.$response.$errors[${index}].height`)"
                    @blur="checkField(`assets.$each.$response.$errors[${index}].height`)"
                  />
                </div>
                <UiSelectElement
                  v-model="form.assets[index]!.language_codes"
                  :label="$t('marketingMaterials.popup.form.language_codes.label')"
                  :placeholder="$t('marketingMaterials.popup.form.language_codes.placeholder')"
                  :list="languages"
                  filterable
                  multiple
                  collapse-tag-tooltip
                  :max-collapse-tags="1"
                  option-value-param="code"
                  search-param="name"
                  required
                />
              </div>
              <UiHelp
                v-if="errors.assets && errors.assets[index]"
                :text="generateErrorMessage(errors.assets[index]!.file_id)"
                is-error
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`marketingMaterials.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading || form.assets.length < 1"
          :loading="!!uploadingFiles"
          @click="handleSave"
        >
          {{ $t(`marketingMaterials.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.marketing-material-popup {
  &__upload {
    .el-upload{
      --el-upload-dragger-padding-vertical: 12px;
      --el-border-color: var(--border-default);
      --el-color-primary: var(--border-focused);
    }
  }
  &__upload-progress {
    width: 40%;
    margin: 0 auto;
    font-size: 14px;
  }
  &__upload-content {
    color: var(--text-body-secondary);
    font-size: 12px;
    font-weight: 400;
    em {
      color: var(--text-link);
    }
  }

  &__assets {
    border-radius: 8px;
    background: var(--background-surface);
    overflow: hidden;
    display: flex;
  }
  &__assets-preview {
    width: 80px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    position: relative;
    img {
      position: absolute;
      width: 100%;
      object-fit: cover;
    }
  }
}
</style>
